import clsx from "clsx";
import type { ReactNode } from "react";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { AgentAvatar } from "@/components/ui/agentic-shared/AgentAvatar";
import type { AgentMessageRole } from "@/components/ui/agentic-shared/types";
import {
  AGENTIC_AGENT_BUBBLE_CLASS,
  AGENTIC_SYSTEM_BUBBLE_CLASS,
  AGENTIC_USER_BUBBLE_CLASS,
} from "@/components/ui/agentic-shared/agenticSurface";

export type AgenticMessageBubbleProps = {
  role?: AgentMessageRole;
  agentName?: string;
  label?: string;
  streaming?: boolean;
  /** When false, omits avatar and name row on agent turns (grouped follow-ups). Default true. */
  showAgentHeader?: boolean;
  /** When false, omits the user avatar row on grouped user follow-ups. Default true. */
  showUserHeader?: boolean;
  userName?: string;
  userImage?: string | null;
  avatarSize?: number;
  children: ReactNode;
  className?: string;
  bubbleClassName?: string;
};

const roleStyles: Record<AgentMessageRole, string> = {
  agent: AGENTIC_AGENT_BUBBLE_CLASS,
  user: AGENTIC_USER_BUBBLE_CLASS,
  system: AGENTIC_SYSTEM_BUBBLE_CLASS,
};

export function AgenticMessageBubble({
  role = "agent",
  agentName = "Agent",
  label,
  streaming = false,
  showAgentHeader = true,
  showUserHeader = true,
  userName,
  userImage,
  avatarSize = 18,
  children,
  className,
  bubbleClassName,
}: AgenticMessageBubbleProps) {
  const showHeader = role === "agent" && showAgentHeader;
  const showUserAvatar = role === "user" && showUserHeader;
  const groupedIndent = avatarSize + 6;

  return (
    <div
      className={clsx("relative max-w-prose", className)}
      style={
        (role === "agent" && !showHeader) || (role === "user" && !showUserAvatar)
          ? { marginLeft: groupedIndent }
          : undefined
      }
      data-testid="agentic-message-bubble"
    >
      {label ? (
        <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
      ) : null}
      <div className={clsx("min-w-0", roleStyles[role], bubbleClassName)}>
        {showHeader ? (
          <div className="mb-1.5 flex items-center gap-1.5">
            <AgentAvatar agentName={agentName} size={avatarSize} className="shrink-0" />
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {agentName}
              {streaming ? (
                <span className="ml-1.5 text-indigo-600 dark:text-indigo-400">typing…</span>
              ) : null}
            </span>
          </div>
        ) : null}
        {showUserAvatar ? (
          <div className="mb-1.5 flex items-center gap-1.5">
            <WorkerAvatar
              seed={userName || "User"}
              size={avatarSize}
              image={userImage}
              user
              className="shrink-0"
            />
            {userName ? (
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                {userName}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}
